import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { useDefaultFilterMenuOptions, useNotesTableColumns } from "@/entities/notes-table-v3";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { formatDayJs } from "@/shared/utils";
import {
  DEFAULT_NOTE_TYPES,
  useDeleteNotesByIdsMutation,
  useGetPersonByIdQuery,
  useLazyGetNoteByIdQuery,
} from "@/services/api";
import { createNewTempNote, type NotesManager } from "@/features/notes-manager";
import { DATE_FORMAT } from "@/shared/constants";
import { useAlert } from "@/shared/hooks";
import { getErrorMessage } from "@/services/errors";
import type { MaybeType } from "@/shared/types";
import type { Key } from "react";
import type { TableRowSelection } from "antd/es/table/interface";
import type { NotesTableItem } from "../types";
import { ideasPageActions, ideasPageSelectors } from "../slice";
import { useTabsManager } from "./use-tabs-manager";
import { useFilters } from "./use-filters";

export const useNotesManager = (): NotesManager => {
  const { personId } = useParams();
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const { t } = useTranslation();

  const [deleteNotesByIds] = useDeleteNotesByIdsMutation();
  const [getNoteById] = useLazyGetNoteByIdQuery();
  const { data: currentPerson } = useGetPersonByIdQuery(
    { id: personId ?? "" },
    { skip: !personId || personId === "TEMP_ID" },
  );

  const { notesTableColumnsGetters } = useNotesTableColumns();
  const { getDefaultPersonGroupOptions, getDefaultNoteTypeOptions, getDefaultNoteStatusOptions } =
    useDefaultFilterMenuOptions();
  const tabsManager = useTabsManager();
  const filters = useFilters();

  const dispatch = useAppDispatch();

  const { callError } = useAlert();

  const titleFilter = useAppSelector(ideasPageSelectors.selectTitleFilter);
  const noteTitleFilter = useAppSelector(ideasPageSelectors.selectNoteTitleFilter);
  const notesTableData = useAppSelector(
    ideasPageSelectors.selectNotesTableDataByPersonId(personId),
  );
  const selectedNotes = useAppSelector(ideasPageSelectors.selectSelectedNotes);
  const isLoading = useAppSelector(ideasPageSelectors.selectIsLoading);
  const selectedIds = useAppSelector(ideasPageSelectors.selectSelectedIds);
  const swipedNote = useAppSelector(ideasPageSelectors.selectSwipedNote);
  const sorter = useAppSelector(ideasPageSelectors.selectSorter);
  const stringFilter = useAppSelector(ideasPageSelectors.selectStringFilter);
  const hasFilters = useAppSelector(ideasPageSelectors.hasFilters);
  const fromDateFilter = useAppSelector(ideasPageSelectors.selectFromDateFilter);
  const toDateFilter = useAppSelector(ideasPageSelectors.selectToDateFilter);
  const personsData = useAppSelector(ideasPageSelectors.selectPersons);
  const activeNote = useAppSelector(ideasPageSelectors.selectActiveNote);

  const columns = useMemo(
    () => [
      notesTableColumnsGetters.getNotesTableImportantColumn({
        title: "",
        dataIndex: ["important"],
      }),
      notesTableColumnsGetters.getNotesTableNoteDateColumn({
        title: t("common.date"),
        dataIndex: ["noteDate"],
        onChangeFilterDateFrom: (date) => filters.setFromDateFilter(formatDayJs(date)),
        onChangeFilterDateTo: (date) => filters.setToDateFilter(formatDayJs(date)),
        colorize: true,
        defaultValueFilterFrom: fromDateFilter ? dayjs(fromDateFilter, DATE_FORMAT) : undefined,
        defaultValueFilterTo: toDateFilter ? dayjs(toDateFilter, DATE_FORMAT) : undefined,
      }),
      notesTableColumnsGetters.getNotesTableNoteTitleColumn({
        dataIndex: ["title"],
        title: t("common.noteType.idea_one"),
        onChangeFilter: filters.setNoteTitleFilter,
        filterValue: noteTitleFilter,
        colorize: true,
      }),
      notesTableColumnsGetters.getNotesTableNoteStatusColumn({
        title: t("common.status.status"),
        dataIndex: ["status"],
        menuFilters: getDefaultNoteStatusOptions(),
        onChangeMenuFilters: (selected: string[]) => filters.setStatusFilters?.(selected),
      }),
    ],
    [
      t,
      filters,
      titleFilter,
      noteTitleFilter,
      getDefaultPersonGroupOptions,
      getDefaultNoteTypeOptions,
      notesTableColumnsGetters,
    ],
  );

  const onSelectChange = useCallback(
    (selectedKeys: Key[]) => {
      dispatch(ideasPageActions.setSelectedIds(selectedKeys));
    },
    [dispatch],
  );

  const rowSelection: TableRowSelection<NotesTableItem> = useMemo(
    () => ({
      selectedNotes,
      onChange: onSelectChange,
      columnWidth: 35,
    }),
    [selectedNotes, onSelectChange],
  );

  const handleClickRow = useCallback(
    (note: NotesTableItem) => {
      getNoteById({ noteId: note.id })
        .unwrap()
        .then((res) => {
          const person = personsData.find((p) => p.id === res.personId) ?? null;
          dispatch(ideasPageActions.addSelectedNote({ ...res, person }));
          dispatch(ideasPageActions.setActiveNote({ noteId: note.id }));
        })
        .catch((e) => {
          callError({ text: getErrorMessage(e.code) });
        });
    },
    [dispatch, getNoteById, personsData],
  );

  const handleAddTempNote = useCallback(() => {
    if (!currentPerson) return;

    const newNote = {
      ...createNewTempNote(),
      title: t("notesPage.newNote"),
      noteType: { name: DEFAULT_NOTE_TYPES.DEFAULT_IDEA },
      person: currentPerson,
      personId: currentPerson?.id ?? "",
    };

    dispatch(ideasPageActions.addSelectedNote(newNote));
    dispatch(ideasPageActions.setActiveNote({ noteId: newNote.id }));
  }, [dispatch, t, currentPerson]);

  const deleteSelected = useCallback(
    (ids: string[]) => {
      deleteNotesByIds(ids)
        .unwrap()
        .then(() => {
          setDeleteModalIsOpen(false);
          dispatch(ideasPageActions.setSelectedNotes([]));
        })
        .catch((e) => {
          callError({ text: getErrorMessage(e.code) });
        });
    },
    [deleteNotesByIds, dispatch],
  );

  const setSelectedNotes = useCallback(
    (notes: NotesTableItem[]) => {
      dispatch(ideasPageActions.setSelectedNotes(notes));
    },
    [dispatch],
  );

  const setSwipedNote = useCallback(
    (noteId: MaybeType<string>) => {
      dispatch(ideasPageActions.setSwipedNote(noteId));
    },
    [dispatch],
  );

  const handleOpenModal = useCallback(() => {
    setDeleteModalIsOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setDeleteModalIsOpen(false);
  }, []);

  const selectedTableNotes = useMemo(() => {
    return notesTableData?.filter((note) => selectedIds.includes(note.id)) ?? [];
  }, [selectedIds, notesTableData]);

  return {
    state: {
      columns,
      notesTableData,
      isLoading,
      selectedTableNotes,
      deleteModalIsOpen,
      deleteDisabled: !selectedTableNotes.length,
      selectedIds,
      swipedNote,
      selectedNotes,
      sorter,
      stringFilter,
      emptyNotes: notesTableData.length === 0 && !hasFilters,
      activeNote,
    },
    methods: {
      rowSelection,
      handleClickRow,
      handleAddTempNote,
      deleteSelected,
      handleCloseModal,
      handleOpenModal,
      onSelectChange,
      upsertNote: tabsManager.methods.upsertNote,
      deleteNoteFileById: tabsManager.methods.deleteNoteFileById,
      setSelectedNotes,
      setSwipedNote,
      setStringFilter: filters.setStringFilter,
    },
    tabsManager,
    filters,
  };
};
